import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, supportedLanguages, defaultLanguage, getTranslation } from '../locales';
import { settingsAPI } from '../utils/api';

const AdminLanguageContext = createContext();

export const useAdminLanguage = () => {
  const context = useContext(AdminLanguageContext);
  if (!context) {
    throw new Error('useAdminLanguage must be used within an AdminLanguageProvider');
  }
  return context;
};

export const AdminLanguageProvider = ({ children, workspaceId }) => {
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current translations
  const currentTranslations = translations[currentLanguage] || translations[defaultLanguage];

  // Translation function with fallback
  const t = (key, fallback) => {
    return getTranslation(currentTranslations, key, fallback || key);
  };

  // Load admin language preference
  useEffect(() => {
    const fetchAdminLanguage = async () => {
      if (!workspaceId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await settingsAPI.getAdminLanguage(workspaceId);
        const data = response.data;
        setCurrentLanguage(data.adminLanguage || defaultLanguage);
      } catch (err) {
        console.error('Error fetching admin language:', err);
        setError(err.message);
        setCurrentLanguage(defaultLanguage); // Fallback to default
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdminLanguage();
  }, [workspaceId]);

  const value = {
    currentLanguage,
    supportedLanguages,
    t,
    isLoading,
    error,
    isAdminLanguage: true // Flag to indicate this is admin language context
  };

  return (
    <AdminLanguageContext.Provider value={value}>
      {children}
    </AdminLanguageContext.Provider>
  );
};

export default AdminLanguageContext;
